/**
 * React Query hooks for team management
 * Provides type-safe data fetching and mutations for teams
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';

import { teamsKeys } from '@/lib/queryClient';
import { api } from '@/services/api';
import type {
  Team,
  TeamMember,
  PendingInvitesResponse,
  InvitePreview,
  AcceptInviteResponse,
  CreateTeamRequest,
  UpdateTeamRequest,
  CreateInviteRequest,
  CreateInviteResponse,
} from '@/types';

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch all teams for the current user
 */
export function useTeams(): UseQueryResult<Team[], Error> {
  return useQuery({
    queryKey: teamsKeys.list(),
    queryFn: async () => {
      const teams = await api.get<Team[]>('/teams');
      return teams;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Fetch a single team by ID
 */
export function useTeam(teamId: string): UseQueryResult<Team, Error> {
  return useQuery({
    queryKey: teamsKeys.detail(teamId),
    queryFn: async () => {
      const team = await api.get<Team>(`/teams/${teamId}`);
      return team;
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch team members
 */
export function useTeamMembers(
  teamId: string
): UseQueryResult<TeamMember[], Error> {
  return useQuery({
    queryKey: [...teamsKeys.detail(teamId), 'members'],
    queryFn: async () => {
      const members = await api.get<TeamMember[]>(`/teams/${teamId}/members`);
      return members;
    },
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new team
 */
export function useCreateTeam(): UseMutationResult<
  Team,
  Error,
  CreateTeamRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTeamRequest) => {
      const team = await api.post<Team>('/teams', data);
      return team;
    },
    onSuccess: (newTeam) => {
      // Invalidate teams list to refetch
      queryClient.invalidateQueries({ queryKey: teamsKeys.lists() });

      // Optimistically add the new team to the cache
      queryClient.setQueryData<Team[]>(teamsKeys.list(), (old) => {
        if (!old) return [newTeam];
        return [...old, newTeam];
      });
    },
    onError: (error) => {
      console.error('Failed to create team:', error);
    },
  });
}

/**
 * Update an existing team
 */
export function useUpdateTeam(
  teamId: string
): UseMutationResult<Team, Error, UpdateTeamRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateTeamRequest) => {
      const team = await api.put<Team>(`/teams/${teamId}`, data);
      return team;
    },
    onSuccess: (updatedTeam) => {
      // Update the team in the detail cache
      queryClient.setQueryData(teamsKeys.detail(teamId), updatedTeam);

      // Update the team in the list cache
      queryClient.setQueryData<Team[]>(teamsKeys.list(), (old) => {
        if (!old) return [updatedTeam];
        return old.map((team) =>
          team.teamId === teamId ? updatedTeam : team
        );
      });
    },
    onError: (error) => {
      console.error('Failed to update team:', error);
    },
  });
}

/**
 * Delete a team
 */
export function useDeleteTeam(
  teamId: string
): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete(`/teams/${teamId}`);
    },
    onSuccess: () => {
      // Remove from list cache
      queryClient.setQueryData<Team[]>(teamsKeys.list(), (old) => {
        if (!old) return [];
        return old.filter((team) => team.teamId !== teamId);
      });

      // Remove detail cache
      queryClient.removeQueries({ queryKey: teamsKeys.detail(teamId) });

      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: teamsKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to delete team:', error);
    },
  });
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Get the user's default/first team
 * Useful for auto-selecting a team when user has teams
 */
export function useDefaultTeam(): {
  team: Team | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const teamsQuery = useTeams();

  return {
    team: teamsQuery.data?.[0],
    isLoading: teamsQuery.isLoading,
    error: teamsQuery.error,
  };
}

/**
 * Check if user has any teams
 */
export function useHasTeams(): {
  hasTeams: boolean;
  isLoading: boolean;
} {
  const { data: teams, isLoading } = useTeams();

  return {
    hasTeams: (teams?.length ?? 0) > 0,
    isLoading,
  };
}

// ============================================================================
// INVITE HOOKS
// ============================================================================

/**
 * Fetch pending invites for a team
 */
export function usePendingInvites(
  teamId: string
): UseQueryResult<PendingInvitesResponse, Error> {
  return useQuery({
    queryKey: [...teamsKeys.detail(teamId), 'invites'],
    queryFn: async () => {
      const response = await api.get<PendingInvitesResponse>(
        `/teams/${teamId}/invites`
      );
      return response;
    },
    enabled: !!teamId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Get invite preview by token (public, no auth required)
 */
export function useInvitePreview(
  token: string
): UseQueryResult<InvitePreview, Error> {
  return useQuery({
    queryKey: ['invites', token],
    queryFn: async () => {
      const response = await api.get<InvitePreview>(`/invites/${token}`, {
        skipAuth: true,
      });
      return response;
    },
    enabled: !!token,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Create a new team invite
 */
export function useCreateInvite(
  teamId: string
): UseMutationResult<CreateInviteResponse, Error, CreateInviteRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateInviteRequest) => {
      const invite = await api.post<CreateInviteResponse>(
        `/teams/${teamId}/invites`,
        data
      );
      return invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...teamsKeys.detail(teamId), 'invites'],
      });
    },
  });
}

/**
 * Resend a team invite
 */
export function useResendInvite(
  teamId: string
): UseMutationResult<CreateInviteResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const invite = await api.post<CreateInviteResponse>(
        `/teams/${teamId}/invites/${encodeURIComponent(email)}/resend`
      );
      return invite;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...teamsKeys.detail(teamId), 'invites'],
      });
    },
  });
}

/**
 * Cancel a pending invite
 */
export function useCancelInvite(
  teamId: string
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      await api.delete(`/teams/${teamId}/invites/${encodeURIComponent(email)}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...teamsKeys.detail(teamId), 'invites'],
      });
    },
    onError: (error) => {
      console.error('Failed to cancel invite:', error);
    },
  });
}

/**
 * Accept a team invite
 */
export function useAcceptInvite(): UseMutationResult<
  AcceptInviteResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (token: string) => {
      const response = await api.post<AcceptInviteResponse>(
        `/invites/${token}/accept`
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate teams list since user may have joined a new team
      queryClient.invalidateQueries({ queryKey: teamsKeys.lists() });
    },
    onError: (error) => {
      console.error('Failed to accept invite:', error);
    },
  });
}
